/* ============================================================
   Antrobus Handcrafted — Bench Sheet
   ------------------------------------------------------------
   Captures the ring layout + enamel colours at Add to Bag, so
   the order carries everything needed to make the piece.

   Included once from layout/theme.liquid. Safe to load on every
   page — it does nothing unless an add-to-cart happens.
   ============================================================ */
(function () {
  'use strict';

  /* ---- Confirmed enamel palette (30 colours) ------------------
     Matched by hex, which is stable. `name` is the manufacturer's
     name from the test plate; `alt` is the name shown on the site
     where the two differ. S03 and S15 are excluded (faulty /
     mislabelled on the test plate).                              */
  var PALETTE = [
    { code: 'S01', hex: '#dedfc9', name: 'White' },
    { code: 'S09', hex: '#e5d7b1', name: 'Ivory' },
    { code: 'S25', hex: '#b0b29d', name: 'Off White' },
    { code: 'S02', hex: '#e6db54', name: 'Yellow' },
    { code: 'S04', hex: '#c65631', name: 'Orange' },
    { code: 'S07', hex: '#c45555', name: 'Dark Red' },
    { code: 'S06', hex: '#a94147', name: 'Deep Red' },
    { code: 'S05', hex: '#e5688e', name: 'Hot Pink' },
    { code: 'S31', hex: '#c5869d', name: 'Dusty Pink' },
    { code: 'S08', hex: '#c4b4b7', name: 'Pastel Pink' },
    { code: 'S12', hex: '#b58aa4', name: 'Royal Berry' },
    { code: 'S10', hex: '#a78293', name: 'Mauve Taupe' },
    { code: 'S11', hex: '#89457e', name: 'Plum Noir' },
    { code: 'S14', hex: '#473d77', name: 'Indigo' },
    { code: 'S13', hex: '#44467c', name: 'Midnight Blue' },
    { code: 'S19', hex: '#31457d', name: 'Cobalt Blue' },
    { code: 'S18', hex: '#2a52b8', name: 'Royal Blue' },
    { code: 'S17', hex: '#157ba0', name: 'Peacock Blue' },
    { code: 'S32', hex: '#236983', name: 'Teal' },
    { code: 'S16', hex: '#79b9c9', name: 'Cerulean Blue' },
    { code: 'S24', hex: '#88bcc0', name: 'Mediterranean Blue' },
    { code: 'S20', hex: '#70c2c5', name: 'Aqua' },
    { code: 'S21', hex: '#679894', name: 'Turquoise Green' },
    { code: 'S23', hex: '#0b8b48', name: 'Forest Green' },
    { code: 'S22', hex: '#21a622', name: 'Lime Green' },
    { code: 'S28', hex: '#654347', name: 'Mahogany' },
    { code: 'S26', hex: '#461e21', name: 'Dark Chocolate' },
    { code: 'S27', hex: '#867875', name: 'Latte' },
    { code: 'S29', hex: '#595b63', name: 'Dark Gray' },
    { code: 'S30', hex: '#1a1a1a', name: 'Black' }
  ];

  var BY_HEX = {}, BY_NAME = {}, BY_CODE = {};
  PALETTE.forEach(function (c) {
    BY_HEX[c.hex.toLowerCase()] = c;
    BY_NAME[c.name.toLowerCase()] = c;
    if (c.alt) BY_NAME[c.alt.toLowerCase()] = c;
    BY_CODE[c.code] = c;
  });

  var BLANK = { code: '--', hex: null, name: 'Silver (uncoloured)' };

  function lookupByHex(hex) {
    if (!hex) return null;
    return BY_HEX[String(hex).toLowerCase()] || null;
  }
  function lookupByName(name) {
    if (!name) return null;
    return BY_NAME[String(name).toLowerCase().trim()] || null;
  }
  function lookupByCode(code) {
    if (!code || code === '--') return BLANK;
    return BY_CODE[code] || null;
  }

  /* ---- Reach page-level `let`/`const` from this separate file --- */
  function pageVar(name) {
    try { return (0, eval)(name); } catch (e) { return undefined; }
  }

  /* ---- Ring numbering: top to bottom, then left to right -------
     Shapes are banded into rows so that rings sitting at roughly
     the same height are numbered across before moving down.      */
  function orderRings(shapes) {
    var idx = shapes.map(function (s, i) { return i; });
    var radiusOf = function (s) { return s.r || s.ry || s.rx || 14; };

    idx.sort(function (a, b) {
      var sa = shapes[a], sb = shapes[b];
      var band = Math.max(radiusOf(sa), radiusOf(sb)) * 1.2;
      if (Math.abs(sa.y - sb.y) > band) return sa.y - sb.y;
      if (sa.x !== sb.x) return sa.x - sb.x;
      return sa.y - sb.y;
    });
    return idx;
  }

  /* ---- Encoding ------------------------------------------------
     v1|Design name|t,x,y,rx,ry,idMM,code; ...
     Compact enough to sit comfortably inside a URL.              */
  function encodeLayout(designName, rings) {
    var parts = rings.map(function (r) {
      return [
        r.t,
        Math.round(r.x * 10) / 10,
        Math.round(r.y * 10) / 10,
        Math.round(r.rx * 10) / 10,
        Math.round(r.ry * 10) / 10,
        r.idMM || '',
        r.code
      ].join(',');
    });
    var raw = 'v1|' + (designName || '') + '|' + parts.join(';');
    return b64urlEncode(raw);
  }

  /* ---- Plain-text ledger, for the emails -----------------------
     Rows separated by ~, fields by |, so Liquid can render it
     without needing to decode anything.
     n|size|colour name|code                                       */
  function encodeLedger(rings) {
    return rings.map(function (r, i) {
      return [
        i + 1,
        r.idMM ? r.idMM.toFixed(1) + ' mm' : '—',
        r.siteName || r.name,   // what the customer chose it by
        r.code,
        r.name                  // manufacturer's name on the test plate
      ].join('|');
    }).join('~');
  }

  /* ---- Ring positions as percentages, for the email diagram ----
     leftPct,topPct,diameterPct — relative to the design's bounding
     box, so an email can draw it with plain positioned divs.      */
  function encodePositions(rings) {
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    rings.forEach(function (r) {
      minX = Math.min(minX, r.x - r.rx); maxX = Math.max(maxX, r.x + r.rx);
      minY = Math.min(minY, r.y - r.ry); maxY = Math.max(maxY, r.y + r.ry);
    });
    var w = maxX - minX, h = maxY - minY;
    var span = Math.max(w, h) || 1;
    // Centre the design inside a square box.
    var offX = (span - w) / 2, offY = (span - h) / 2;

    return rings.map(function (r) {
      var d = (r.rx * 2 / span) * 100;
      var left = ((r.x - r.rx - minX + offX) / span) * 100;
      var top  = ((r.y - r.ry - minY + offY) / span) * 100;
      return [
        Math.round(left * 10) / 10,
        Math.round(top * 10) / 10,
        Math.round(d * 10) / 10
      ].join(',');
    }).join('~');
  }

  function decodeLayout(token) {
    try {
      var raw = b64urlDecode(token);
      var bits = raw.split('|');
      if (bits[0] !== 'v1') return null;
      var rings = (bits[2] || '').split(';').filter(Boolean).map(function (chunk, i) {
        var f = chunk.split(',');
        var colour = lookupByCode(f[6]) || BLANK;
        return {
          n: i + 1,
          t: f[0],
          x: parseFloat(f[1]),
          y: parseFloat(f[2]),
          rx: parseFloat(f[3]),
          ry: parseFloat(f[4]),
          idMM: f[5] ? parseFloat(f[5]) : null,
          code: f[6],
          colour: colour
        };
      });
      return { design: bits[1] || '', rings: rings };
    } catch (e) {
      return null;
    }
  }

  function b64urlEncode(str) {
    var b64 = btoa(unescape(encodeURIComponent(str)));
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function b64urlDecode(token) {
    var b64 = String(token).replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    return decodeURIComponent(escape(atob(b64)));
  }

  /* ---- Build the layout from whatever the design page holds ---- */
  function buildLayout() {
    var design = pageVar('activeDesign');
    var colours = pageVar('ringColours');
    var shapesFor = pageVar('enamelShapesFor');

    if (!design || !colours || typeof shapesFor !== 'function') return null;

    var shapes;
    try { shapes = shapesFor(design); } catch (e) { return null; }
    if (!shapes || !shapes.length) return null;

    var order = orderRings(shapes);

    // Design-tool shape coordinates are in tenths of a millimetre —
    // divide by 10 so the bench sheet (and its 1:1 scale print) reads
    // in true mm. Confirmed against Halo: raw bounding box 152 design
    // units vs. widthMM 15.2mm stored on the design.
    var MM_SCALE = 0.1;

    var rings = order.map(function (originalIndex) {
      var s = shapes[originalIndex];
      var hex = colours[originalIndex];
      var colour = lookupByHex(hex) || BLANK;
      if (hex && colour === BLANK) {
        // A colour was chosen but its hex isn't in the manufacturing
        // palette above — usually means a product page's swatch hex
        // has drifted from bench-sheet.js's PALETTE. Surface it loudly
        // rather than silently sending "Silver (uncoloured)" to the bench.
        console.warn('[bench-sheet] No palette match for chosen colour ' + hex + ' — check this page\'s COLOURS array against PALETTE in bench-sheet.js.');
      }
      return {
        t: s.shapeType === 'oval' ? 'o' : (s.shapeType === 'teardrop' ? 'd' : 'c'),
        x: s.x * MM_SCALE,
        y: s.y * MM_SCALE,
        rx: (s.rx || s.r || 14) * MM_SCALE,
        ry: (s.ry || s.r || 14) * MM_SCALE,
        idMM: s.idMM || null,
        code: colour.code,
        name: colour.name,
        siteName: colour.alt || colour.name
      };
    });

    return { design: design.name || '', rings: rings };
  }

  // Raw, re-importable design — native design-tool units (tenths of mm),
  // exact shape geometry, exact enamel hex per shape. This is what the
  // standalone desktop designer's "Import Design File" reads, so the
  // bench print comes straight from the same renderer used to design
  // and sell the piece — no re-drawing, no guesswork.
  function buildImportableDesign() {
    var design = pageVar('activeDesign');
    var colours = pageVar('ringColours');
    var shapesFor = pageVar('enamelShapesFor');
    if (!design || !colours || typeof shapesFor !== 'function') return null;

    var enamelShapes;
    try { enamelShapes = shapesFor(design); } catch (e) { enamelShapes = null; }
    if (!enamelShapes) return null;

    // Map colours (indexed against enamelShapes) back onto the *full*
    // shapes array design.shapes, since balls and other non-enamel
    // shapes are excluded from enamelShapes but still need to be in
    // the exported file so the piece reconstructs correctly.
    var fullShapes = (design.shapes || []).map(function (s) {
      var idx = enamelShapes.indexOf(s);
      var hex = idx > -1 ? colours[idx] : null;
      var copy = {};
      for (var k in s) { if (Object.prototype.hasOwnProperty.call(s, k)) copy[k] = s[k]; }
      copy.enamel = hex || null;
      return copy;
    });

    return {
      name: design.name || 'Untitled',
      type: design.type || 'stud',
      shapes: fullShapes
    };
  }

  /* ---- Intercept Add to Bag ------------------------------------
     The product pages already POST to /cart/add.js with
     "Ring 1..N" properties in design order. We renumber those to
     the bench order and attach the layout as a hidden property.  */
  var nativeFetch = window.fetch;
  if (typeof nativeFetch !== 'function') return;

  window.fetch = function (input, init) {
    try {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var isAdd = url.indexOf('/cart/add.js') !== -1;

      if (isAdd && init && init.body && typeof init.body === 'string') {
        var payload = JSON.parse(init.body);
        var layout = buildLayout();

        if (layout && payload && payload.items && payload.items.length) {
          payload.items.forEach(function (item) {
            item.properties = item.properties || {};

            // Drop the original design-order ring numbering.
            Object.keys(item.properties).forEach(function (k) {
              if (/^Ring \d+$/.test(k)) delete item.properties[k];
            });

            // Renumbered, bench order: top to bottom, left to right.
            // Shown to the customer, so use the name they chose it by.
            layout.rings.forEach(function (r, i) {
              item.properties['Ring ' + (i + 1)] = r.siteName || r.name;
            });

            // Hidden from the customer, carried through to the order.
            item.properties['_bench']     = encodeLayout(layout.design, layout.rings);
            item.properties['_bench_led'] = encodeLedger(layout.rings);
            item.properties['_bench_pos'] = encodePositions(layout.rings);

            // Raw re-importable design, for opening directly in the
            // standalone desktop designer. Only attached when available;
            // never blocks the order if something upstream is missing.
            try {
              var importable = buildImportableDesign();
              if (importable) {
                item.properties['_bench_import'] = JSON.stringify(importable);
              }
            } catch (e) {
              console.warn('[bench-sheet] Could not build importable design file.', e);
            }
          });

          init = Object.assign({}, init, { body: JSON.stringify(payload) });
        } else if (!layout) {
          console.warn('[bench-sheet] Layout unavailable — order will carry colours but no diagram.');
        }
      }
    } catch (e) {
      console.error('[bench-sheet] Capture failed, add to bag continuing unchanged:', e);
    }

    return nativeFetch.call(this, input, init);
  };

  /* ---- Public surface (used by the bench sheet page) ----------- */
  window.AntrobusBench = {
    palette: PALETTE,
    decode: decodeLayout,
    encode: encodeLayout,
    encodeLedger: encodeLedger,
    encodePositions: encodePositions,
    lookupByCode: lookupByCode,
    lookupByName: lookupByName,
    lookupByHex: lookupByHex,
    buildLayout: buildLayout
  };
})();